import React, { FC, useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import ProjectPageShell, { Crumb } from "../shared/ProjectPageShell";
import ActivityInDetailView from "./ActivityInDetailsView";
import { RootState } from "../../../store/store";
import { updateActivityInDetailModalSlice } from "../../../store/slices/projectManagement/activity-view/ActivityInDetailView";
import { activityApis } from "../../../api/Activity";
import { useWbsName } from "../shared/useWbsCodes";

const ActivityDetailPage: FC = () => {
  const { activityId } = useParams<{ activityId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const wbsName = useWbsName();

  const [loading, setLoading] = useState(true);
  const [activityData, setActivityData] = useState<any>(null);

  // Get data from location state
  const locationState = location.state as any;
  const projectId = locationState?.projectId;
  const projectName = locationState?.projectName;
  const phaseId = locationState?.phaseId;
  const phaseName = locationState?.phaseName;
  // Generic return path - allows navigation back to any source page
  const returnPath = locationState?.returnPath;
  const returnState = locationState?.returnState;
  const derivedProjectStatus = locationState?.extra?.screenName;

  const { data: ActivityInDetail } = useSelector(
    (state: RootState) => state.ActivityInDetailModal
  );

  // Get projectId from Redux layout state as fallback
  const { currentProjectId, currentProjectName } = useSelector(
    (state: RootState) => state.layout
  );

  // Use Redux projectId as fallback when location state doesn't have it
  const resolvedProjectId = projectId || currentProjectId;
  const resolvedProjectName = projectName || currentProjectName;

  // Fetch activity details
  useEffect(() => {
    const fetchActivityDetails = async () => {
      if (!activityId) return;

      setLoading(true);
      try {
        const response = await activityApis.fetchActivityById(
          Number(activityId),
          derivedProjectStatus
        );
        if (response?.status && response?.data) {
          const activity = response.data;
          setActivityData(activity);

          // Update Redux state for child components
          dispatch(
            updateActivityInDetailModalSlice({
              open: true,
              id: activity.activityId,
              activityName: activity.activityName,
              activeTab: "tasks",
              allowEdit: true,
              allowAdd: true,
              projectResponse: {
                id: activity.projectId || resolvedProjectId,
                name: resolvedProjectName || activity.projectName || "Project",
                status: activity.projectStatus || "Active",
              },
              phaseResponse: {
                id: activity.phaseId || phaseId,
                name: phaseName || activity.phaseName || "Phase",
                status: activity.phaseStatus || "Active",
              },
              extra: {
                status: activity.status,
                startDate: activity.activityStartDate,
                endDate: activity.activityEndDate,
                ownerName: activity.activityOwnerName,
                createdAt: activity.createdAt,
                createdByUserName: activity.createdByUserName,
              },
            })
          );
        }
      } catch (error) {
        console.error("Error fetching activity details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchActivityDetails();
  }, [activityId, dispatch, resolvedProjectId, resolvedProjectName, phaseId, phaseName]);

  // Handle back navigation - uses returnPath if available
  const handleBack = () => {
    if (returnPath) {
      navigate(returnPath, { state: returnState });
    } else if (phaseId) {
      navigate(`/pms/project/phase-detail/${phaseId}`, {
        state: {
          ...locationState,
          projectId: resolvedProjectId,
          projectName: resolvedProjectName,
        },
      });
    } else {
      navigate(`/pms/project/activities/${resolvedProjectId}`, {
        state: locationState,
      });
    }
  };

  // Handle navigation back to phase
  const handleBackToPhase = () => {
    navigate(`/pms/project/phase-detail/${phaseId}`, {
      state: {
        ...locationState,
        projectId: resolvedProjectId,
        projectName: resolvedProjectName,
      },
    });
  };

  // Handle navigation back to phases list
  const handleBackToPhases = () => {
    navigate(`/pms/project/phases/${resolvedProjectId}`, {
      state: locationState,
    });
  };

  // Handle navigation back to project
  const handleBackToProject = () => {
    navigate(`/pms/project/dashboard/${resolvedProjectId}`, {
      state: locationState,
    });
  };

  const activityName = wbsName(
    activityData?.activityName || ActivityInDetail?.activityName || "Activity"
  );

  const breadcrumb: Crumb[] = [
    { label: resolvedProjectName || "Project", onClick: handleBackToProject },
    { label: "Phases", onClick: handleBackToPhases },
    { label: wbsName(phaseName) || "Phase", onClick: handleBackToPhase },
    { label: activityName },
  ];

  return (
    <ProjectPageShell
      headTitle={`${activityName} - Activity Details`}
      breadcrumb={breadcrumb}
      title={activityName}
      eyebrow="Activity"
      onBack={handleBack}
      loading={loading}
    >
      <ActivityInDetailView id={Number(activityId)} activeTab="tasks" />
    </ProjectPageShell>
  );
};

export default ActivityDetailPage;
